import { LineChart } from "@mui/x-charts";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import dayjs from "dayjs";
import { SettingsStatisticsConfiguration } from "../../../../../utils/configurationProvider";

const CommonLineGraph = ({
  graphDataFirst,
  graphDataSecond,
  xAxisLabels, // e.g. ["2025-01-01", "2025-01-02", ..., "2025-02-01"] or ["Sun", "Mon"]
  graphDataFirstColor,
  graphDataSecondColor = "var(--text-primary)",
  defaultDataItemsForXaxis,
  period,
  amountFormatRequiredForLegend = true,
  amountFormatForLegend,
}) => {
  let xAxisArray = xAxisLabels;

  const formattedGraphDataFirst = graphDataFirst.map((value) => value);
  const formattedGraphDataSecond = graphDataSecond
    ? graphDataSecond.map((value) => value)
    : [];

  // --- Dynamic min/max ---
  const allData = [...formattedGraphDataFirst, ...formattedGraphDataSecond];
  const dataMax = Math.max(...allData);

  // If all data is zero, max should fallback to 1 to avoid chart centering
  const yAxisMin = 0;
  const yAxisMax = dataMax > 0 ? dataMax * 1.2 : 1;

  // --- CSS vars ---
  const getCssVar = (variable) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim();

  const graphDataFirstColorForSeries = getCssVar(
    graphDataFirstColor.replace(/var\(|\)/g, ""),
  );
  const graphDataSecondColorForSeries = getCssVar(
    graphDataSecondColor.replace(/var\(|\)/g, ""),
  );

  const monthGroups = [];
  if (period === "last 28 days") {
    // --- Group dates by month ---
    let currentMonth = null;

    xAxisLabels.forEach((date, index) => {
      const month = dayjs(date).format("MMM YYYY");
      if (!currentMonth || currentMonth.name !== month) {
        currentMonth = { name: month, start: index, end: index };
        monthGroups.push(currentMonth);
      } else {
        currentMonth.end = index;
      }
    });
  }

  // for week days logic wrtten
  xAxisArray = xAxisLabels.map(
    (xAxisLabels) => xAxisLabels.slice(0, 3), // just first 3 letters
  );

  return (
    <div className="relative">
      <LineChart
        grid={{ vertical: true }}
        tooltip={{ trigger: "item" }}
        margin={{
          right: SettingsStatisticsConfiguration.paddingFromBothSide, // right padding
          left: SettingsStatisticsConfiguration.paddingFromBothSide, // left padding
        }}
        series={
          graphDataSecond
            ? [
                {
                  id: "first",
                  data: formattedGraphDataFirst,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataFirstColorForSeries,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
                {
                  id: "second",
                  data: formattedGraphDataSecond,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataSecondColorForSeries,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
              ]
            : [
                {
                  id: "first",
                  data: formattedGraphDataFirst,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataFirstColorForSeries,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
              ]
        }
        className={`settings-statistics-linechart`}
        xAxis={[
          {
            valueFormatter: (value) =>
              period === "last 28 days"
                ? dayjs(xAxisArray[value]).format("DD")
                : xAxisArray[value],
            data: defaultDataItemsForXaxis,
            tickMinStep: 1,
            min: -0.5,
            max: xAxisArray.length - 0.5,
            showGrid: true,
          },
        ]}
        yAxis={[{ min: yAxisMin, max: yAxisMax }]}
        style={{
          height: "var(--statistic-graph-height)",
          width: "var(--width-full)",
        }}
        sx={{
          // ---------------First series styles----------------//
          "& .MuiLineElement-series-first": {
            stroke: graphDataFirstColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-first": {
            fill: graphDataFirstColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          /* Styling of marks for first*/
          "& .MuiMarkElement-series-first": {
            fill: "var(--text-primary)",
            stroke: graphDataFirstColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },
          "& .MuiMarkElement-series-first:hover": {
            fill: "var(--text-primary)",
            stroke: graphDataFirstColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },

          // -----------Second series styles----------------//
          "& .MuiLineElement-series-second": {
            stroke: graphDataSecondColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-second": {
            fill: graphDataSecondColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          /* Styling of marks for second*/
          "& .MuiMarkElement-series-second": {
            fill: "var(--text-primary)",
            stroke: graphDataSecondColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },
          "& .MuiMarkElement-series-second:hover": {
            fill: "var(--text-primary)",
            stroke: graphDataSecondColor,
            strokeWidth:
              SettingsStatisticsConfiguration.strokeWidthForLineGraph,
            r: SettingsStatisticsConfiguration.radiusOfMarkForLineGraph,
          },
          // ------------Common styling---------------------//

          "& .MuiChartsAxisHighlight-root": {
            strokeDasharray:
              SettingsStatisticsConfiguration.zeroStrokeWidthForLineGraph,
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            stroke: "var(--text-primary)",
          },
          "& .MuiChartsAxis-root line": {
            stroke: "var(--text-primary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
          },
          "& .MuiChartsAxis-tick text": {
            fill: "var(--text-primary)",
            fontSize: "var(--font-sm)",
          },
          "& .MuiChartsAxis-tickLabel": {
            fill: "var(--text-primary) !important",
          },

          // hide horizontal grid lines
          "& .MuiChartsGrid-horizontalLine": {
            display: "none",
          },

          // hide Y-axis baseline
          "& .MuiChartsAxis-left": {
            display: "none",
          },

          //styling vertical line
          "& .MuiChartsGrid-verticalLine": {
            stroke: "var(--text-secondary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            opacity: SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
        }}
      />

      {period === "last 28 days" ? (
        /* Month labels row */
        <div className="absolute bottom-bottom-base left-left-base right-right-base flex justify-between px-padding-xl">
          {monthGroups.map((group, idx) => {
            const widthPercent =
              ((group.end - group.start + 1) / xAxisLabels.length) * 100;

            return (
              <div
                key={idx}
                style={{
                  width: `${widthPercent}%`,
                  margin: "var(--margin-base) var(--margin-xs)", // space between months
                  textAlign: "center",
                }}
              >
                {/* Horizontal line with end bars */}
                <div
                  style={{
                    position: "relative",
                    height: "var(--border)",
                    background: "var(--text-secondary)",
                    width: "var(--width-full)",
                    marginBottom: "var(--margin-xs)",
                  }}
                >
                  {/* Left bar */}
                  <div
                    style={{
                      position: "absolute",
                      left: "var(--left-base)",
                      top: "-1.5px",
                      width: "var(--border)",
                      height: "6px",
                      background: "var(--text-secondary)",
                    }}
                  ></div>

                  {/* Right bar */}
                  <div
                    style={{
                      position: "absolute",
                      right: "var(--right-base)",
                      top: "-1.5px",
                      width: "var(--border)",
                      height: "6px",
                      background: "var(--text-secondary)",
                    }}
                  ></div>
                </div>

                {/* Month label */}
                <div
                  style={{
                    fontSize: "var(--font-sm)",
                    color: "var(--text-primary)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {group.name}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default CommonLineGraph;
